import {Component, Emit, Prop, Vue, Watch} from 'vue-property-decorator';
import { HarvestShipping, HarvestShippingItem } from '@/types/Component';
import Modal from '@/assets/common/Modal/Modal.vue';
import { toCamel, toSnake } from 'snake-camel';

@Component({
    components: {
        Modal
    }
})
export default class EditModal extends Vue {
    private title = ''
    private modalTitle = ''
    private items: HarvestShippingItem[] = []
    private isAdd = false
    private isEdit = false
    private isWeightUnits = false
    private isWorkUnits = false
    private itemId = ''
    private newName = ''
    private newWeightPerUnit: number | null = null
    private addCandidateModalVisible = false
    private updatingTarget: HarvestShippingItem | null = null
    private checked = false;
    private withHidden = false;


    @Prop()
    private baseUrl!: string

    @Prop()
    private value!: boolean

    @Watch('showEditModal')
    private async onShowEditModalChange() {
        if(this.showEditModal) {
            await this.getItems()
        }
    }

    @Emit('updateItems')
    private updateItems() {
        return this.baseUrl
    }

    @Emit('closeEditModal')
    private closeEditModal() {
        this.newName = ''
        this.newWeightPerUnit = null
        this.isAdd = false
        this.isEdit = false
        this.items = []
    }

    @Emit('input')
    private input(value: boolean) {
        return value
    }

    get showEditModal() {
        return this.value
    }

    set showEditModal(value: boolean) {
        this.input(value)
    }

    private async getItems() {
        this.isWeightUnits = false
        await this.$apiClient.doRequest('get', `${this.baseUrl}?with_hidden=${this.checked}`).then(ret => {
            const item = toCamel(ret) as HarvestShipping
            switch (this.baseUrl) {
                case '/weight_units':
                    this.title = '単位'
                    this.items = item.weightUnits
                    this.isWeightUnits = true
                    break
                case '/measurement_timings':
                    this.title = '計測タイミング'
                    this.items = item.measurementTimings
                    break
                case '/quality_standards':
                    this.title = '品質・規格'
                    this.items = item.qualityStandards
                    break
                case '/work_units':
                    this.title = '単位'
                    this.items = item.workUnits
                    this.isWorkUnits = true
                    break
            }
            this.modalTitle = this.title + 'の設定'
        })
    }

    private addItem() {
        this.isAdd = true
        this.isEdit = true
        this.newName = ''
        this.newWeightPerUnit = null
        this.updatingTarget = null
        this.addCandidateModalVisible = true
    }

    private edit(item: HarvestShippingItem) {
        this.isEdit = true
        this.itemId = item.id ? item.id : ''
        this.newName = item.name
        this.newWeightPerUnit = item.weightPerUnit ? item.weightPerUnit : null
        this.updatingTarget = item
    }

    private cancelEdit() {
        this.updatingTarget = null
        this.newName = ''
        this.newWeightPerUnit = null
        this.isAdd = false
        this.isEdit = false
    }

    private validate() {
        const errmsg: string[] = []

        // 名前の長さ
        if(this.newName.length > 255){
            errmsg.push(`${this.title}名は255文字以内で入力してください（現在は${this.newName.length}文字）`)
        }

        // 数量
        if(this.isWeightUnits && this.newWeightPerUnit){
            const isMinus: boolean = /-/.test(this.newWeightPerUnit.toString())
            const integer: string = this.newWeightPerUnit.toString().split('.')[0]
            const decimal: string = this.newWeightPerUnit.toString().split('.')[1]
            if(isMinus || integer.length > 7 || (decimal && decimal.length > 3)){
                errmsg.push('重量は0より大きい整数部7桁小数部3桁までの数値を入力してください')
            }
        }

        if(errmsg.length > 0) {
            window.alert(errmsg.join('\n'))
            return false
        }

        return true
    }

    private doUpdate(item: HarvestShippingItem){
        if(!item && !this.validate()){
            return;
        }
        const params: HarvestShippingItem = {
            name: this.newName.trim(),
            isHidden: false
        }
        let resultMessage = '変更を保存しました。'
        if(item) {
            if(window.confirm('非表示にしますか？')){
                this.itemId = item.id ? item.id : ''
                params.name = item.name.trim()
                params.isHidden = true
                resultMessage = '非表示にしました。'
            }else {
                return
            }
        }
        if(this.baseUrl == '/weight_units') params.weightPerUnit = this.newWeightPerUnit
        const sendParams = toSnake(params)
        let method: "post" | "put" = 'put'
        let url = this.baseUrl + '/' + this.itemId
        if(this.isAdd) {
            method = 'post'
            url = this.baseUrl
        }
        this.$apiClient.doRequest(method, url, sendParams).then(ret => {
            this.isAdd = false
            this.isEdit = false
            this.newName = ''
            this.newWeightPerUnit = null
            this.updatingTarget = null
            this.getItems().then(() => {
                this.updateItems()
            })
            this.$toasted.success(resultMessage)
        })
        .catch(error => {
            this.isAdd = false;
            this.$toasted.error('変更に失敗しました。')
        })
    }

    private doDelete(item: any) {
        if (item.workUnitExists) {
            return window.alert(`単位「${item.name}」を使用したデータが存在する為、削除できません。`);
        }
        const confirm = window.confirm('削除しますか？')
        if(confirm) {
            this.$apiClient.doRequest('delete', `${this.baseUrl}/${item.id}`)
                .then(() => {
                    this.getItems().then(() => {
                        this.updateItems()
                    })
                    this.$toasted.success('削除しました。')
                })
                .catch(() => {
                    this.$toasted.error('削除に失敗しました。')
                })
        } else {
            this.$toasted.success('削除がキャンセルされました。')
        }
    }

    private doUpdateFlag(item: HarvestShippingItem){
        const params: HarvestShippingItem = {
            name: this.newName,
            isHidden: true
        }
        let resultMessage = '変更を保存しました。'
        if(item) {
            if(window.confirm('表示にしますか？')){
                this.itemId = item.id ? item.id : ''
                params.name = item.name
                params.isHidden = false
                resultMessage = '表示にしました。'
            }else {
                return
            }
        }
        const sendParams = toSnake(params)
        const  url = this.baseUrl + '/' + this.itemId
        this.$apiClient.doRequest('put', url, sendParams).then(ret => {
            this.isAdd = false
            this.isEdit = false
            this.newName = ''
            this.newWeightPerUnit = null
            this.updatingTarget = null
            this.getItems().then(() => {
                this.updateItems()
            })
            this.$toasted.success(resultMessage)
        })
        .catch(error => {
            this.$toasted.error('変更に失敗しました。')
        })
    }

    @Emit('checkboxChanged')
    private checkboxChanged() {
        this.getItems();
        return this.checked;
    }
}